import React, { useMemo } from 'react'
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel
} from '@mui/material'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import {
  ColumnDef,
  createExpandedRowModel,
  createSortedRowModel,
  ExpandedState,
  rowExpandingFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  tableFeatures,
  useTable
} from '@tanstack/react-table'

import { Nested, nestByHierarchy } from '../../helpers/memberReport'

export const treeTableFeatures = tableFeatures({
  rowSortingFeature,
  rowExpandingFeature,
  sortedRowModel: createSortedRowModel(),
  expandedRowModel: createExpandedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, basic: sortFn_basic },
  columnMeta: {} as { width?: number | string; minWidth?: number }
})

type TreeRow = { id: string; hierarchy: string[] }

export type TreeColumnDef<T extends TreeRow> = ColumnDef<
  typeof treeTableFeatures,
  T,
  any // eslint-disable-line @typescript-eslint/no-explicit-any
>

const INDENT = 3

// Sortable, expandable table for rows keyed by `hierarchy`. The first column
// carries the indentation and expand toggle; siblings sort among themselves.
const TreeTable = <T extends TreeRow>({
  rows,
  columns,
  expandedDepth,
  emptyMessage
}: {
  rows: T[]
  columns: TreeColumnDef<T>[]
  // Levels open initially: 0 = collapsed, 1 = roots open, -1 = everything.
  expandedDepth: number
  // Shown in place of the rows when there are none.
  emptyMessage?: string
}) => {
  const data = useMemo(() => nestByHierarchy(rows), [rows])

  const initialExpanded = useMemo<ExpandedState>(() => {
    if (expandedDepth < 0) return true
    return Object.fromEntries(
      rows
        .filter(row => row.hierarchy.length <= expandedDepth)
        .map(row => [row.id, true])
    )
    // Only the first render's rows matter; later changes keep user toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const table = useTable({
    features: treeTableFeatures,
    data,
    columns,
    getRowId: row => row.id,
    getSubRows: row => (row as Nested<T>).subRows,
    initialState: { expanded: initialExpanded },
    autoResetExpanded: false
  })

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Table stickyHeader size="small">
        <TableHead>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const { column } = header
                const sorted = column.getIsSorted()
                return (
                  <TableCell
                    key={header.id}
                    sx={{
                      // Chrome can leave a sub-pixel gap above a sticky
                      // header that rows show through while scrolling; lift
                      // the header and paint over whatever is left.
                      top: -4,
                      boxShadow: '0 -4px 0 0 white',
                      width: column.columnDef.meta?.width,
                      minWidth: column.columnDef.meta?.minWidth
                    }}
                    sortDirection={sorted || false}
                  >
                    <TableSortLabel
                      active={Boolean(sorted)}
                      direction={sorted || 'asc'}
                      onClick={column.getToggleSortingHandler()}
                    >
                      <table.FlexRender header={header} />
                    </TableSortLabel>
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {emptyMessage && rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id} hover>
              {row.getAllCells().map((cell, index) => (
                <TableCell
                  key={cell.id}
                  // The shared theme disables text selection in table cells;
                  // report values (emails, paths) should be copyable.
                  sx={{ userSelect: 'text' }}
                >
                  {index === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        pl: row.depth * INDENT
                      }}
                    >
                      {row.getCanExpand() ? (
                        <IconButton
                          size="small"
                          aria-label={
                            row.getIsExpanded() ? 'Collapse' : 'Expand'
                          }
                          onClick={row.getToggleExpandedHandler()}
                        >
                          {row.getIsExpanded() ? (
                            <KeyboardArrowDown fontSize="small" />
                          ) : (
                            <KeyboardArrowRight fontSize="small" />
                          )}
                        </IconButton>
                      ) : (
                        // Keeps leaf labels aligned with their siblings'.
                        <Box sx={{ width: 34, flexShrink: 0 }} />
                      )}
                      <table.FlexRender cell={cell} />
                    </Box>
                  ) : (
                    <table.FlexRender cell={cell} />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default TreeTable
